import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import RadioInput from 'LuxKit/Radio/RadioInput'
import SwitchButton from 'LuxKit/Switch/SwitchButton'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { isRewardFareClass, type FareClassWithReward, type FlightsClassTypes } from 'constants/flight'
import { getCharterFareCabinLabel, getCharterFareClassDisplayLabel } from 'hooks/Flights/useCharterFlightLabels'
import config from 'constants/config'
import { LUXURY_ESCAPES } from 'constants/brands'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useIntl } from 'react-intl'
import MaldivesCharterRewardSeatsToggle from './MaldivesCharterRewardSeatsToggle'
import MaldivesCharterInfiniteMemberPriceToggle from './MaldivesCharterInfiniteMemberPriceToggle'
import { useRewardSeatsEnabled } from 'hooks/Flights/useRewardSeatsEnabled'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import { getCharterSelectableCashCabins } from 'lib/maldivesCharter/charterCabins'

const RadioGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

/**
 * Outer wrapper for the calendar header actions. On mobile the controls sit on two
 * horizontal-scroll rows: the primary chips/toggles on top, and the reward / Infinite
 * Member Price / price-mode controls beneath. Both rows scroll horizontally rather than
 * wrapping, so the filter set stays two rows even when all controls are present (e.g. for
 * Infinite members). On tablet+ everything stays inline.
 */
const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  align-items: flex-start;
  gap: ${rem(8)};

  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
  }
`

const PrimaryControlsRow = styled(HorizontalContainerWithFadedOverflow)`
  max-width: 100%;
`

const RewardControlsRow = styled(HorizontalContainerWithFadedOverflow)`
  max-width: 100%;
`

interface Props {
  nights: number
  onDurationChange: (nights: number) => void
  durationOptions: ReadonlyArray<{ value: number, label: string }>
  cabinFilter?: FlightsClassTypes | null
  onCabinFilterChange?: (cabin: FlightsClassTypes | null) => void
  luxPlusEnabled?: boolean
  onLuxPlusToggle?: (enabled: boolean) => void
  availableFareClasses?: ReadonlyArray<FareClassWithReward>
  rewardSeatsOnly?: boolean
  onRewardSeatsOnlyChange?: (enabled: boolean) => void
  infiniteMemberOnly?: boolean
  onInfiniteMemberOnlyChange?: (enabled: boolean) => void
  priceMode?: 'total' | 'perPerson'
  onPriceModeChange?: (priceMode: 'total' | 'perPerson') => void
}

/**
 * Header actions for Charter Bundle Calendar Modal
 * Contains duration dropdown and LuxPlus toggle
 */
function CharterBundleCalendarActions(props: Props) {
  const {
    nights,
    onDurationChange,
    durationOptions,
    cabinFilter,
    onCabinFilterChange,
    luxPlusEnabled = true,
    onLuxPlusToggle,
    availableFareClasses = EmptyArray,
    rewardSeatsOnly = false,
    onRewardSeatsOnlyChange,
    infiniteMemberOnly = false,
    onInfiniteMemberOnlyChange,
    priceMode,
    onPriceModeChange,
  } = props
  const isRewardSeatsEnabled = useRewardSeatsEnabled()
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const showRewardSeatsToggle = isRewardSeatsEnabled && !!onRewardSeatsOnlyChange
  const showPriceModeToggle = !!priceMode && !!onPriceModeChange
  // Infinite Member Price checkbox lives beside the price-mode control. On desktop the
  // price-mode control is rendered separately (HotelPriceToggle), so the checkbox is gated
  // to where price mode is present (mobile actions); the desktop copy renders in
  // MaldivesDirectSearchFlightCalendar's "Total price" row. Only shown to Infinite members.
  const showInfiniteMemberToggle = isInfiniteTier && !!onInfiniteMemberOnlyChange && showPriceModeToggle

  const intl = useIntl()
  const isLuxRowEnabled = useFeatureFlag(OptimizelyFeatureFlags.luxRow)

  const fareClassOptions = useMemo<ReadonlyArray<FareClassWithReward>>(() => {
    // Calendar dropdown is cash-only: reward variants are picked inside the
    // per-date fare carousel / cabin modal, not at the calendar filter level.
    const cashOnly = availableFareClasses.filter(fc => !isRewardFareClass(fc))
    if (cashOnly.length > 0) {
      return cashOnly
    }
    return getCharterSelectableCashCabins(isLuxRowEnabled)
  }, [availableFareClasses, isLuxRowEnabled])

  // "All classes" was previously tied to whether reward variants were in the dropdown.
  // Now that rewards are filtered out, show it whenever there are multiple cabins to choose between.
  const showAllClassesOption = fareClassOptions.length > 1

  const getFareClassOptionLabel = useCallback((fc: FareClassWithReward): string => {
    return getCharterFareClassDisplayLabel(intl, fc)
  }, [intl])

  const [isFareClassDropdownOpen, setIsFareClassDropdownOpen] = useState(false)
  const fareClassTriggerRef = useRef<HTMLButtonElement>(null)

  const [isDurationDropdownOpen, setIsDurationDropdownOpen] = useState(false)
  const durationTriggerRef = useRef<HTMLButtonElement>(null)

  const [isPriceModeDropdownOpen, setIsPriceModeDropdownOpen] = useState(false)
  const priceModeTriggerRef = useRef<HTMLButtonElement>(null)

  // Auto-switch to available duration if current selection is no longer available
  // This handles the case where a user's selected duration sells out
  useEffect(() => {
    const isCurrentDurationAvailable = durationOptions.some(opt => opt.value === nights)
    if (!isCurrentDurationAvailable && durationOptions.length > 0) {
      onDurationChange(durationOptions[0].value)
    }
  }, [nights, durationOptions, onDurationChange])

  const handleDurationChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newDuration = parseInt(event.target.value, 10)
    onDurationChange(newDuration)
    setIsDurationDropdownOpen(false)
  }, [onDurationChange])

  const toggleDurationDropdown = useCallback(() => {
    setIsDurationDropdownOpen(prev => !prev)
  }, [])

  const closeDurationDropdown = useCallback(() => {
    setIsDurationDropdownOpen(false)
  }, [])

  const handleLuxPlusToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onLuxPlusToggle?.(event.currentTarget.checked)
  }, [onLuxPlusToggle])

  const handlePriceModeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onPriceModeChange?.(event.currentTarget.value as 'total' | 'perPerson')
    setIsPriceModeDropdownOpen(false)
  }, [onPriceModeChange])

  const togglePriceModeDropdown = useCallback(() => {
    setIsPriceModeDropdownOpen(prev => !prev)
  }, [])

  const closePriceModeDropdown = useCallback(() => {
    setIsPriceModeDropdownOpen(false)
  }, [])

  const canOpenFareClassDropdown = fareClassOptions.length > 1
  const toggleFareClassDropdown = useCallback(() => {
    if (!canOpenFareClassDropdown) {
      return
    }
    setIsFareClassDropdownOpen(prev => !prev)
  }, [canOpenFareClassDropdown])

  const closeFareClassDropdown = useCallback(() => {
    setIsFareClassDropdownOpen(false)
  }, [])

  const handleFareClassChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value
    if (value === 'all') {
      onCabinFilterChange?.(null)
    } else {
      onCabinFilterChange?.(value as FlightsClassTypes)
    }
    setIsFareClassDropdownOpen(false)
  }, [onCabinFilterChange])

  const allClassesLabel = intl.formatMessage({
    id: 'kx--uuQIXCpg8TnLp8jT',
    defaultMessage: 'All classes',
  })
  const selectCabinLabel = intl.formatMessage({
    id: 'Y7XGF9bZxNQEK89br8_j',
    defaultMessage: 'Select cabin',
  })
  const defaultDurationLabel = intl.formatMessage({
    id: 'FDr1lx5JJcBxDolKWLaU',
    defaultMessage: '5 nights & flights',
  })
  const totalPriceLabel = intl.formatMessage({
    id: 'kGgDSvtOWXZi9MJV-MQC',
    defaultMessage: 'Total price',
  })
  const perPersonLabel = intl.formatMessage({
    id: 'nrkVKqo7sbL3FTKdBcdQ',
    defaultMessage: 'Per person',
  })

  const fareClassChipLabel = cabinFilter
    ? getCharterFareCabinLabel(intl, cabinFilter)
    : (showAllClassesOption ? allClassesLabel : selectCabinLabel)
  const selectedPriceModeLabel = priceMode === 'perPerson' ? perPersonLabel : totalPriceLabel

  const shouldShowFareClassFilter = canOpenFareClassDropdown && !!onCabinFilterChange

  return (
    <ActionsWrapper>
      <PrimaryControlsRow verticalAlign="center" gap={8}>
        {shouldShowFareClassFilter && (
          <div>
            <FilterChip
              size="medium"
              onClick={toggleFareClassDropdown}
              endIcon={<DropdownChevron open={isFareClassDropdownOpen} />}
              ref={fareClassTriggerRef}
            >
              {fareClassChipLabel}
            </FilterChip>
            {shouldShowFareClassFilter && (
              <DropdownList
                size="S"
                open={isFareClassDropdownOpen}
                onClose={closeFareClassDropdown}
                anchorRef={fareClassTriggerRef}
                triggerRef={fareClassTriggerRef}
                portaled
              >
                <RadioGroup direction="vertical" gap={16}>
                  {showAllClassesOption && (
                    <RadioInput
                      checked={!cabinFilter}
                      onChange={handleFareClassChange}
                      name="charterFareClass"
                      value="all"
                    >
                      {allClassesLabel}
                    </RadioInput>
                  )}
                  {fareClassOptions.map((option) => (
                    <RadioInput
                      key={option}
                      checked={cabinFilter === option}
                      onChange={handleFareClassChange}
                      name="charterFareClass"
                      value={option}
                    >
                      {getFareClassOptionLabel(option)}
                    </RadioInput>
                  ))}
                </RadioGroup>
              </DropdownList>
            )}
          </div>
        )}
        {/* Only show duration dropdown if there are multiple duration options */}
        {durationOptions.length > 1 && (
          <div>
            <FilterChip
              size="medium"
              onClick={toggleDurationDropdown}
              endIcon={<DropdownChevron open={isDurationDropdownOpen} />}
              ref={durationTriggerRef}
            >
              {durationOptions.find(option => option.value === nights)?.label || defaultDurationLabel}
            </FilterChip>
            <DropdownList
              size="S"
              open={isDurationDropdownOpen}
              onClose={closeDurationDropdown}
              anchorRef={durationTriggerRef}
              triggerRef={durationTriggerRef}
              portaled
            >
              <RadioGroup direction="vertical" gap={16}>
                {durationOptions.map((option) => (
                  <RadioInput
                    key={option.value}
                    checked={nights === option.value}
                    onChange={handleDurationChange}
                    name="durationNights"
                    value={option.value}
                  >
                    {option.label}
                  </RadioInput>
                ))}
              </RadioGroup>
            </DropdownList>
          </div>
        )}
        {config.BRAND === LUXURY_ESCAPES && (
          <ProductPaletteProvider palette="lux-plus">
            <SwitchButton
              checked={luxPlusEnabled}
              data-testid="luxPlus-toggle"
              onChange={handleLuxPlusToggle}
            >
              <LuxPlusLogo inline height={10} />
            </SwitchButton>
          </ProductPaletteProvider>
        )}
      </PrimaryControlsRow>
      {(showRewardSeatsToggle || showPriceModeToggle || showInfiniteMemberToggle) && (
        <RewardControlsRow verticalAlign="center" gap={8}>
          {showInfiniteMemberToggle && onInfiniteMemberOnlyChange && (
            <MaldivesCharterInfiniteMemberPriceToggle
              checked={infiniteMemberOnly}
              onChange={onInfiniteMemberOnlyChange}
            />
          )}
          {showRewardSeatsToggle && (
            <MaldivesCharterRewardSeatsToggle
              checked={rewardSeatsOnly}
              onChange={onRewardSeatsOnlyChange}
            />
          )}
          {showPriceModeToggle && (
            <div>
              <FilterChip
                size="medium"
                onClick={togglePriceModeDropdown}
                endIcon={<DropdownChevron open={isPriceModeDropdownOpen} />}
                ref={priceModeTriggerRef}
              >
                {selectedPriceModeLabel}
              </FilterChip>
              <DropdownList
                size="S"
                open={isPriceModeDropdownOpen}
                onClose={closePriceModeDropdown}
                anchorRef={priceModeTriggerRef}
                triggerRef={priceModeTriggerRef}
                portaled
              >
                <RadioGroup direction="vertical" gap={16}>
                  <RadioInput
                    checked={priceMode === 'total'}
                    onChange={handlePriceModeChange}
                    name="charterPriceMode"
                    value="total"
                  >
                    {totalPriceLabel}
                  </RadioInput>
                  <RadioInput
                    checked={priceMode === 'perPerson'}
                    onChange={handlePriceModeChange}
                    name="charterPriceMode"
                    value="perPerson"
                  >
                    {perPersonLabel}
                  </RadioInput>
                </RadioGroup>
              </DropdownList>
            </div>
          )}
        </RewardControlsRow>
      )}
    </ActionsWrapper>
  )
}

export default CharterBundleCalendarActions
